import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { VehicleTriplePicker } from "../lib/vehicleCatalogue.jsx";

/**
 * VR creation dialog.
 *
 * Props:
 *   enums              — from /api/enums
 *   presetCustomerId   — if opened from a customer profile page
 *   presetCustomerName — used to render the read-only pill when preset
 *   onClose, onCreated
 */
export default function NewVehicleRegistrationDialog({ enums, presetCustomerId, presetCustomerName, onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [form, setForm] = useState({
    customer_id: presetCustomerId || "",
    registration_plate: "",
    vin: "",
    brand: enums.brands[0] || "",
    model: "",
    vehicle_generation: "",
    colour: "",
    year: new Date().getFullYear(),
    engine_capacity: "",
    transmission: "",
    fuel_type: "",
    warranty_start_date: new Date().toISOString().slice(0, 10),
    warranty_duration_months: 24,
    warranty_end_date: "",
    sale_date: new Date().toISOString().slice(0, 10),
    branch: enums.branches[0] || "",
    salesperson_id: "",
    alidata_sale_reference: "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/users").then((r) => setSalespeople(r.data.filter((u) => !u.deactivated)));
  }, []);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year, 10),
        warranty_duration_months: parseInt(form.warranty_duration_months, 10),
        warranty_end_date: form.warranty_end_date || null,
        transmission: form.transmission || null,
        fuel_type: form.fuel_type || null,
        salesperson_id: form.salesperson_id || null,
        engine_capacity: form.engine_capacity || null,
        alidata_sale_reference: form.alidata_sale_reference || null,
        notes: form.notes || null,
      };
      const { data } = await api.post("/vehicle-registrations", payload);
      onCreated?.(data);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-vr-dialog">
        <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Registar viatura</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F4F6F8] rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          {presetCustomerId ? (
            <div className="col-span-2 bg-[#F4F6F8] border border-gray-200 rounded-sm px-3 py-2 text-xs">
              <span className="text-[#6C757D]">Cliente:</span>{" "}
              <span className="font-medium">{presetCustomerName}</span>
            </div>
          ) : (
            <div className="col-span-2">
              <Field label="Cliente *">
                <select value={form.customer_id} onChange={(e) => change("customer_id", e.target.value)} className={inputCls} data-testid="vr-customer-select">
                  <option value="">Seleccionar cliente…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_number} · {c.name} · {c.phone}</option>)}
                </select>
              </Field>
            </div>
          )}

          <Field label="Matrícula * (ABC-1234-M)">
            <input value={form.registration_plate} onChange={(e) => change("registration_plate", e.target.value.toUpperCase())} className={inputCls + " font-mono"} data-testid="vr-plate-input" placeholder="ABC-1234-M" />
          </Field>
          <Field label="VIN *">
            <input value={form.vin} onChange={(e) => change("vin", e.target.value.toUpperCase())} className={inputCls + " font-mono"} data-testid="vr-vin-input" />
          </Field>
          <div className="col-span-2">
            <label className="text-xs text-[#6C757D] block mb-1">Viatura (Marca · Modelo · Geração) *</label>
            <VehicleTriplePicker
              brand={form.brand}
              model={form.model}
              generation={form.vehicle_generation}
              onChange={({ brand, model, generation }) =>
                setForm((f) => ({ ...f, brand: brand || "", model: model || "", vehicle_generation: generation || "" }))
              }
              required
              testidPrefix="vr"
            />
          </div>
          <Field label="Cor *">
            <input value={form.colour} onChange={(e) => change("colour", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Ano *">
            <input type="number" value={form.year} onChange={(e) => change("year", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Cilindrada">
            <input value={form.engine_capacity} onChange={(e) => change("engine_capacity", e.target.value)} placeholder="Ex.: 2.0 TDI" className={inputCls} />
          </Field>
          <Field label="Transmissão">
            <select value={form.transmission} onChange={(e) => change("transmission", e.target.value)} className={inputCls}>
              <option value="">—</option>
              <option>Manual</option><option>Automático</option>
            </select>
          </Field>
          <Field label="Combustível">
            <select value={form.fuel_type} onChange={(e) => change("fuel_type", e.target.value)} className={inputCls}>
              <option value="">—</option>
              <option>Gasolina</option><option>Diesel</option><option>Eléctrico</option><option>Híbrido</option>
            </select>
          </Field>
          <Field label="Data da venda *">
            <input type="date" value={form.sale_date} onChange={(e) => change("sale_date", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Início da garantia *">
            <input type="date" value={form.warranty_start_date} onChange={(e) => change("warranty_start_date", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Duração da garantia (meses) *">
            <input type="number" value={form.warranty_duration_months} onChange={(e) => change("warranty_duration_months", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Fim da garantia (auto-calculado se vazio)">
            <input type="date" value={form.warranty_end_date} onChange={(e) => change("warranty_end_date", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Dealer *">
            <select value={form.branch} onChange={(e) => change("branch", e.target.value)} className={inputCls}>
              {enums.branches.map((b) => <option key={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Vendedor">
            <select value={form.salesperson_id} onChange={(e) => change("salesperson_id", e.target.value)} className={inputCls}>
              <option value="">—</option>
              {salespeople.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Referência de venda Alidata">
            <input value={form.alidata_sale_reference} onChange={(e) => change("alidata_sale_reference", e.target.value)} className={inputCls} />
          </Field>
          <div className="col-span-2">
            <Field label="Notas">
              <textarea value={form.notes} onChange={(e) => change("notes", e.target.value)} className={inputCls + " h-20"} />
            </Field>
          </div>
        </div>
        {error && <div className="mx-5 mb-3 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="new-vr-error">{error}</div>}
        <div className="sticky bottom-0 bg-white px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancelar</button>
          <button onClick={submit} disabled={submitting} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60" data-testid="new-vr-submit">
            {submitting ? "A guardar…" : "Registar viatura"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}
